import { useState, useEffect } from "react";
import { useAuth } from "@/contexts/AuthContext";
import api from "@/lib/api";
import {
  Buildings, Users, Briefcase, Target, Phone, TrendUp, ChartBar, ArrowRight,
  Kanban, Calendar, CurrencyGbp, Cube, Lightning, Trophy, Medal
} from "@phosphor-icons/react";
import { useNavigate } from "react-router-dom";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from "recharts";
import TeamStatusWidget from "@/components/TeamStatusWidget";

function StatCard({ label, value, icon: Icon, color, onClick }) {
  return (
    <button onClick={onClick} className="bm-stat-card text-left w-full" data-testid={`stat-${label.toLowerCase().replace(/\s/g, "-")}`}>
      <div className="flex items-start justify-between">
        <div>
          <p className="text-[10px] text-[#6B7280] font-medium uppercase tracking-wide">{label}</p>
          <p className="text-xl font-bold mt-1" style={{ color: color || "#0F172A", fontFamily: "'Cabinet Grotesk', sans-serif" }}>{value}</p>
        </div>
        <div className="w-9 h-9 rounded-lg flex items-center justify-center" style={{ backgroundColor: `${color}10` }}>
          <Icon size={20} weight="duotone" style={{ color }} />
        </div>
      </div>
    </button>
  );
}

export default function DashboardPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState({});
  const [chartData, setChartData] = useState([]);
  const [activity, setActivity] = useState([]);
  const [pipelineStats, setPipelineStats] = useState(null);
  const [interviewStats, setInterviewStats] = useState(null);
  const [revenue, setRevenue] = useState(null);
  const [leaderboard, setLeaderboard] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        const [statsRes, chartRes, activityRes, pipeRes, intRes, revRes, lbRes] = await Promise.all([
          api.get("/dashboard/stats"),
          api.get("/dashboard/chart-data"),
          api.get("/dashboard/activity?limit=8"),
          api.get("/dashboard/pipeline-stats").catch(() => ({ data: null })),
          api.get("/dashboard/interview-stats").catch(() => ({ data: null })),
          api.get("/dashboard/revenue-forecast").catch(() => ({ data: null })),
          api.get("/dashboard/leaderboard").catch(() => ({ data: { leaderboard: [] } })),
        ]);
        setStats(statsRes.data);
        setChartData(chartRes.data);
        setActivity(activityRes.data);
        setPipelineStats(pipeRes.data);
        setInterviewStats(intRes.data);
        setRevenue(revRes.data);
        setLeaderboard(lbRes.data?.leaderboard || []);
      } catch (err) {
        console.error("Dashboard error:", err);
      } finally { setLoading(false); }
    };
    load();
  }, []);

  if (loading) return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">{[1,2,3,4,5,6,7,8].map(i => <div key={i} className="h-20 skeleton-shimmer rounded-lg" />)}</div>
      <div className="h-48 skeleton-shimmer rounded-lg" />
    </div>
  );

  const roleLabel = { admin: "Administrator", team_leader: "Team Leader", opportunity_finder: "Opportunity Finder", candidate_finder: "Candidate Finder", senior_recruitment_partner: "Senior Recruitment Partner" }[user?.role] || "";

  const coreStats = [
    ...(user?.role === "admin" || user?.role === "team_leader" ? [{ key: "active_vacancies", label: "Active Vacancies", icon: Cube, color: "#0F172A", nav: "/vacancies" }] : []),
    { key: "placements", label: "In Pipeline", icon: Kanban, color: "#8B5CF6", nav: "/pipeline" },
    { key: "placed", label: "Placed", icon: TrendUp, color: "#16A34A", nav: "/pipeline" },
    { key: "upcoming_interviews", label: "Upcoming Int.", icon: Calendar, color: "#06B6D4", nav: "/interviews" },
    ...(user?.role === "admin" || user?.role === "team_leader" || user?.role === "opportunity_finder" ? [{ key: "companies", label: "Companies", icon: Buildings, color: "#1E3A8A", nav: "/companies" }] : []),
    ...(user?.role === "admin" || user?.role === "team_leader" || user?.role === "candidate_finder" ? [{ key: "candidates", label: "Candidates", icon: Users, color: "#C5A059", nav: "/candidates" }] : []),
    { key: "leads", label: "Leads", icon: Target, color: "#10B981", nav: "/leads" },
    { key: "calls", label: "Calls", icon: Phone, color: "#F59E0B", nav: "/call-logs" },
  ];

  return (
    <div data-testid="dashboard-page" className="space-y-5">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl md:text-2xl font-bold text-[#0F172A]">Welcome back, {user?.name}</h1>
          <p className="text-sm text-[#6B7280] mt-0.5">{roleLabel} Dashboard</p>
        </div>
        <button onClick={() => navigate("/hub/analytics")} className="text-xs text-[#1E3A8A] hover:underline font-medium flex items-center gap-1" data-testid="view-analytics-link">
          <ChartBar size={14} /> Full Analytics
        </button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {coreStats.map(s => (
          <StatCard key={s.key} label={s.label} value={stats[s.key] ?? 0} icon={s.icon} color={s.color} onClick={() => navigate(s.nav)} />
        ))}
      </div>

      {/* Revenue + Pipeline Summary */}
      {revenue && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div className="bg-white rounded-lg border border-[#E5E7EB] p-4">
            <p className="text-[10px] text-[#6B7280] font-medium uppercase">Weighted Forecast</p>
            <p className="text-2xl font-bold text-[#1E3A8A] mt-1" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
              {"\u00a3"}{revenue.weighted_forecast.toLocaleString("en-GB", { minimumFractionDigits: 0 })}
            </p>
          </div>
          <div className="bg-white rounded-lg border border-[#E5E7EB] p-4">
            <p className="text-[10px] text-[#6B7280] font-medium uppercase">Confirmed Revenue</p>
            <p className="text-2xl font-bold text-[#16A34A] mt-1" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
              {"\u00a3"}{revenue.confirmed_revenue.toLocaleString("en-GB", { minimumFractionDigits: 0 })}
            </p>
          </div>
          <div className="bg-white rounded-lg border border-[#E5E7EB] p-4">
            <p className="text-[10px] text-[#6B7280] font-medium uppercase">Total Pipeline Value</p>
            <p className="text-2xl font-bold text-[#C5A059] mt-1" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
              {"\u00a3"}{revenue.total_potential_revenue.toLocaleString("en-GB", { minimumFractionDigits: 0 })}
            </p>
          </div>
        </div>
      )}

      {/* AI Recruitment Intelligence Card */}
      {(user?.role === "admin" || user?.role === "team_leader") && (
        <div className="bg-gradient-to-br from-[#0F172A] to-[#1E293B] rounded-xl p-5 text-white cursor-pointer hover:shadow-lg transition-shadow"
          onClick={() => navigate("/hub/intelligence")} data-testid="intelligence-dashboard-card">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-[#C5A059]/20 flex items-center justify-center">
                <Lightning size={22} weight="duotone" className="text-[#C5A059]" />
              </div>
              <div>
                <h2 className="text-base font-bold">Talent Engine</h2>
                <p className="text-white/50 text-xs">AI-powered market opportunities for your team</p>
              </div>
            </div>
            <ArrowRight size={20} className="text-[#C5A059]" />
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Weekly Activity Chart */}
        <div className="bg-white rounded-lg border border-[#E5E7EB] p-4">
          <h2 className="text-sm font-semibold text-[#0F172A] mb-3">Weekly Activity</h2>
          <ResponsiveContainer width="100%" height={200}>
            <BarChart data={chartData} barGap={4}>
              <CartesianGrid strokeDasharray="3 3" stroke="#F3F4F6" />
              <XAxis dataKey="day" tick={{ fontSize: 11, fill: "#6B7280" }} />
              <YAxis tick={{ fontSize: 11, fill: "#6B7280" }} />
              <Tooltip contentStyle={{ borderRadius: 8, border: "1px solid #E5E7EB", fontSize: 12 }} />
              <Legend wrapperStyle={{ fontSize: 11 }} />
              <Bar dataKey="calls" fill="#1E3A8A" radius={[4, 4, 0, 0]} name="Calls" />
              <Bar dataKey="leads" fill="#C5A059" radius={[4, 4, 0, 0]} name="Leads" />
              <Bar dataKey="placements" fill="#10B981" radius={[4, 4, 0, 0]} name="Placements" />
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* Interview Stats */}
        {interviewStats && (
          <div className="bg-white rounded-lg border border-[#E5E7EB] p-4">
            <h2 className="text-sm font-semibold text-[#0F172A] mb-3">Interview Overview</h2>
            <div className="grid grid-cols-3 gap-3 mb-3">
              <div className="bg-[#F9FAFB] rounded-lg p-2.5 text-center">
                <p className="text-lg font-bold text-[#0F172A]">{interviewStats.total}</p>
                <p className="text-[10px] text-[#6B7280]">Total</p>
              </div>
              <div className="bg-[#F0FFF4] rounded-lg p-2.5 text-center">
                <p className="text-lg font-bold text-[#16A34A]">{interviewStats.pass_rate}%</p>
                <p className="text-[10px] text-[#6B7280]">Pass Rate</p>
              </div>
              <div className="bg-[#F0F4FF] rounded-lg p-2.5 text-center">
                <p className="text-lg font-bold text-[#1E3A8A]">{interviewStats.avg_score || "—"}</p>
                <p className="text-[10px] text-[#6B7280]">Avg Score</p>
              </div>
            </div>
            <div className="flex flex-wrap gap-2">
              {Object.entries(interviewStats.by_status || {}).map(([status, count]) => count > 0 && (
                <div key={status} className="flex items-center gap-1.5 text-xs">
                  <div className={`w-2 h-2 rounded-full ${status === "completed" ? "bg-green-500" : status === "scheduled" ? "bg-blue-500" : status === "confirmed" ? "bg-teal-500" : status === "cancelled" ? "bg-gray-400" : "bg-red-500"}`} />
                  <span className="text-[#6B7280] capitalize">{status.replace("_", " ")}: <span className="font-medium text-[#0F172A]">{count}</span></span>
                </div>
              ))}
            </div>
            {interviewStats.upcoming?.length > 0 && (
              <div className="mt-3 pt-3 border-t border-[#F3F4F6]">
                <p className="text-[10px] text-[#6B7280] font-medium uppercase mb-1.5">Next Up</p>
                {interviewStats.upcoming.slice(0, 3).map(int => (
                  <div key={int.id} className="flex items-center justify-between py-1.5">
                    <p className="text-xs text-[#0F172A] font-medium truncate">{int.candidate_name}</p>
                    <p className="text-[10px] text-[#6B7280] flex-shrink-0 ml-2">{int.scheduled_at ? new Date(int.scheduled_at).toLocaleDateString() : ""}</p>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Pipeline Funnel Mini */}
      {pipelineStats && pipelineStats.total_placements > 0 && (
        <div className="bg-white rounded-lg border border-[#E5E7EB] p-4">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-sm font-semibold text-[#0F172A]">Pipeline Conversion</h2>
            {pipelineStats.avg_time_to_fill_days > 0 && (
              <span className="text-xs text-[#6B7280]">Avg time: <span className="font-medium text-[#0F172A]">{pipelineStats.avg_time_to_fill_days}d</span></span>
            )}
          </div>
          <div className="flex gap-0.5 overflow-x-auto pb-1">
            {pipelineStats.funnel.filter(f => f.count > 0).map(f => (
              <div key={f.stage} className="flex-shrink-0 text-center min-w-[56px]">
                <div className="h-12 bg-[#0F172A] rounded-t flex items-center justify-center" style={{ opacity: Math.max(0.15, f.conversion_rate / 100) }}>
                  <span className="text-white text-xs font-bold">{f.count}</span>
                </div>
                <p className="text-[8px] text-[#6B7280] mt-1 leading-tight">{f.label}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Performance Leaderboard */}
      {leaderboard.length > 0 && (
        <div className="bg-white rounded-lg border border-[#E5E7EB] p-4" data-testid="leaderboard-section">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-sm font-semibold text-[#0F172A] flex items-center gap-2">
              <Trophy size={16} className="text-[#C5A059]" weight="duotone" /> Performance Leaderboard
            </h2>
            <span className="text-[10px] text-[#6B7280] uppercase tracking-wider font-medium">Placements &middot; Commission</span>
          </div>
          <div className="space-y-1">
            {leaderboard.slice(0, 10).map((entry) => {
              const rankIcon = entry.rank === 1 ? "🥇" : entry.rank === 2 ? "🥈" : entry.rank === 3 ? "🥉" : null;
              const roleLabel = { opportunity_finder: "OF", candidate_finder: "CF", team_leader: "TL", admin: "Admin", senior_recruitment_partner: "SRP" }[entry.role] || "";
              return (
                <div
                  key={entry.user_id}
                  className={`flex items-center gap-3 py-2.5 px-3 rounded-lg transition-colors ${
                    entry.rank <= 3 ? "bg-[#FFFBEB]" : "hover:bg-[#F9FAFB]"
                  }`}
                  data-testid={`lb-row-${entry.rank}`}
                >
                  <div className="w-7 text-center flex-shrink-0">
                    {rankIcon ? (
                      <span className="text-base">{rankIcon}</span>
                    ) : (
                      <span className="text-xs font-bold text-[#9CA3AF]">#{entry.rank}</span>
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-semibold text-[#0F172A] truncate">{entry.name}</span>
                      <span className="text-[10px] px-1.5 py-0.5 rounded bg-[#F1F5F9] text-[#6B7280] font-medium shrink-0">{roleLabel}</span>
                    </div>
                    {entry.team_name && (
                      <p className="text-[10px] text-[#9CA3AF] truncate">{entry.team_name}</p>
                    )}
                  </div>
                  <div className="text-right flex-shrink-0">
                    <p className="text-sm font-bold text-[#0F172A]">{entry.placements} <span className="text-[10px] font-normal text-[#6B7280]">placements</span></p>
                    <p className="text-[10px] text-[#C5A059] font-semibold">{"\u00a3"}{entry.commission.toLocaleString("en-GB", { minimumFractionDigits: 0 })}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Team Working Status */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2">
          <TeamStatusWidget compact={true} />
        </div>
        <div>
          {/* Recent Activity */}
          <div className="bg-white rounded-lg border border-[#E5E7EB] p-4">
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-sm font-semibold text-[#0F172A]">Recent Activity</h2>
              <button onClick={() => navigate("/hub/activity-log")} className="text-xs text-[#1E3A8A] hover:underline">View All</button>
            </div>
        {activity.length === 0 ? (
          <p className="text-sm text-[#6B7280] py-4 text-center">No recent activity</p>
        ) : (
          <div className="space-y-2">
            {activity.map((a, i) => (
              <div key={a.id || i} className="flex items-center gap-3 py-1.5 border-b border-[#F3F4F6] last:border-0">
                <div className="w-7 h-7 rounded-full bg-[#F3F4F6] flex items-center justify-center flex-shrink-0">
                  <Lightning size={12} className="text-[#C5A059]" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-xs text-[#0F172A] truncate">
                    <span className="font-medium">{a.action?.replace(/_/g, " ")}</span> on <span className="text-[#1E3A8A]">{a.resource_type}</span>
                  </p>
                  <p className="text-[10px] text-[#6B7280]">{a.created_at ? new Date(a.created_at).toLocaleString() : ""}</p>
                </div>
              </div>
            ))}
          </div>
        )}
          </div>
        </div>
      </div>
    </div>
  );
}
